/**
 * HTTP Client - Core abstraction for making HTTP requests
 * Implements interceptors, retry logic, timeout handling, and error transformation
 */

import {
  ApiError,
  NetworkError,
  TimeoutError,
  ErrorStatusCode,
  ApiErrorResponse,
  ApiErrorType,
} from './errors';
import { requestDeduplicator } from './request-deduplicator';
import { responseCache } from './response-cache';

export interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';
  headers?: Record<string, string>;
  body?: unknown;
  timeout?: number; // milliseconds
  signal?: AbortSignal;
  credentials?: RequestCredentials;
  skipRetry?: boolean;
  retryCount?: number;
  skipDedup?: boolean; // Skip request deduplication for this request
  cache?: {
    ttl?: number; // Time-to-live in milliseconds (only for GET requests)
    force?: boolean; // Force fetch even if cached
  };
}

export interface HttpResponse<T = unknown> {
  data: T;
  status: number;
  headers: Record<string, string>;
  ok: boolean;
}

export type RequestInterceptor = (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;
export type ResponseInterceptor<T = unknown> = (response: HttpResponse<T>) => HttpResponse<T> | Promise<HttpResponse<T>>;
export type ErrorInterceptor = (error: ApiError) => ApiError | Promise<ApiError>;

export interface HttpClientConfig {
  baseURL?: string;
  timeout?: number;
  maxRetries?: number;
  retryDelay?: number;
  retryBackoffMultiplier?: number;
}

/**
 * Core HTTP Client with interceptor support
 */
export class HttpClient {
  private baseURL: string;
  private timeout: number;
  private maxRetries: number;
  private retryDelay: number;
  private retryBackoffMultiplier: number;

  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];

  constructor(config: HttpClientConfig = {}) {
    this.baseURL = config.baseURL || '';
    this.timeout = config.timeout || 30000;
    this.maxRetries = config.maxRetries || 3;
    this.retryDelay = config.retryDelay || 1000;
    this.retryBackoffMultiplier = config.retryBackoffMultiplier || 2;
  }

  /**
   * Register a request interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Register a response interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * Register an error interceptor
   */
  addErrorInterceptor(interceptor: ErrorInterceptor): void {
    this.errorInterceptors.push(interceptor);
  }

  /**
   * Make HTTP request with retry logic and error handling
   * Includes request deduplication to prevent duplicate in-flight requests
   */
  async request<T = unknown>(
    url: string,
    config: RequestConfig = {},
  ): Promise<HttpResponse<T>> {
    // Check if deduplication should be used (enabled by default)
    if (!config.skipDedup) {
      return requestDeduplicator.executeWithDedup<T>(
        config.method || 'GET',
        url,
        config.body,
        () => this.executeRequest<T>(url, config),
      );
    }

    return this.executeRequest<T>(url, config);
  }

  /**
   * Execute the actual HTTP request with retry logic
   * This is called by the deduplicator or directly if deduplication is skipped
   */
  private async executeRequest<T = unknown>(
    url: string,
    config: RequestConfig = {},
  ): Promise<HttpResponse<T>> {
    const method = config.method || 'GET';

    // Check cache for GET requests (unless forced)
    if (method === 'GET' && !config.cache?.force) {
      const cached = responseCache.get<HttpResponse<T>>(method, url, config.body);
      if (cached) {
        return cached;
      }
    }

    let finalConfig = config;
    let attempt = 0;
    let lastError: ApiError | null = null;

    const maxAttempts = config.skipRetry ? 1 : this.maxRetries + 1;

    while (attempt < maxAttempts) {
      try {
        // Apply request interceptors
        finalConfig = await this.applyRequestInterceptors(finalConfig);

        // Make the request
        const response = await this.fetch<T>(url, finalConfig);

        // Cache successful GET responses
        if (method === 'GET') {
          const cacheTTL = config.cache?.ttl;
          responseCache.set(method, url, response, cacheTTL, config.body);
        }

        // Apply response interceptors
        return await this.applyResponseInterceptors(response);
      } catch (error) {
        lastError = await this.handleError(error as ApiError);

        // Check if we should retry
        if (lastError.isRetryable && attempt < maxAttempts - 1) {
          const delayMs = this.retryDelay * Math.pow(this.retryBackoffMultiplier, attempt);
          await this.sleep(delayMs);
          attempt++;
          continue;
        }

        // No more retries - throw the error after interceptors
        throw await this.applyErrorInterceptors(lastError);
      }
    }

    // Should not reach here, but just in case
    throw lastError || new ApiError('Unknown error');
  }

  /**
   * GET request
   */
  get<T = unknown>(url: string, config?: RequestConfig): Promise<HttpResponse<T>> {
    return this.request<T>(url, { ...config, method: 'GET' });
  }

  /**
   * POST request
   */
  post<T = unknown>(url: string, body?: unknown, config?: RequestConfig): Promise<HttpResponse<T>> {
    return this.request<T>(url, { ...config, method: 'POST', body });
  }

  /**
   * PUT request
   */
  put<T = unknown>(url: string, body?: unknown, config?: RequestConfig): Promise<HttpResponse<T>> {
    return this.request<T>(url, { ...config, method: 'PUT', body });
  }

  /**
   * PATCH request
   */
  patch<T = unknown>(url: string, body?: unknown, config?: RequestConfig): Promise<HttpResponse<T>> {
    return this.request<T>(url, { ...config, method: 'PATCH', body });
  }

  /**
   * DELETE request
   */
  delete<T = unknown>(url: string, config?: RequestConfig): Promise<HttpResponse<T>> {
    return this.request<T>(url, { ...config, method: 'DELETE' });
  }

  /**
   * Internal fetch implementation
   */
  private async fetch<T>(url: string, config: RequestConfig): Promise<HttpResponse<T>> {
    const fullUrl = this.buildUrl(url);
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), config.timeout || this.timeout);

    try {
      const fetchResponse = await fetch(fullUrl, {
        method: config.method || 'GET',
        headers: config.headers,
        body: config.body ? JSON.stringify(config.body) : undefined,
        signal: config.signal || abortController.signal,
        credentials: config.credentials || 'include',
      });

      const headers = this.extractHeaders(fetchResponse.headers);
      const data = await this.parseResponse<T>(fetchResponse);

      if (!fetchResponse.ok) {
        throw this.createApiError(data, fetchResponse.status);
      }

      return {
        data,
        status: fetchResponse.status,
        headers,
        ok: true,
      };
    } catch (error) {
      // Handle abort/timeout
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new TimeoutError(`Request timeout after ${config.timeout || this.timeout}ms`);
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new NetworkError(error.message);
      }

      // Re-throw API errors
      if (error instanceof ApiError) {
        throw error;
      }

      throw new NetworkError(String(error));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Parse response body
   */
  private async parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      try {
        return await response.json();
      } catch {
        return {} as T;
      }
    }

    if (contentType?.includes('text')) {
      return (await response.text()) as unknown as T;
    }

    return {} as T;
  }

  /**
   * Create API error from response
   */
  private createApiError(data: unknown, statusCode: number): ApiError {
    const response = this.parseErrorResponse(data);
    const type = this.getErrorType(statusCode);
    const message = response.message || response.error || 'API request failed';

    return new ApiError(message, statusCode, response, type);
  }

  /**
   * Parse error response
   */
  private parseErrorResponse(data: unknown): ApiErrorResponse {
    if (!data || typeof data !== 'object') {
      return {};
    }

    const obj = data as Record<string, unknown>;
    return {
      error: typeof obj.error === 'string' ? obj.error : undefined,
      message: typeof obj.message === 'string' ? obj.message : undefined,
      code: typeof obj.code === 'string' ? obj.code : undefined,
      details: typeof obj.details === 'object' ? (obj.details as Record<string, unknown>) : undefined,
    };
  }

  /**
   * Determine error type from status code
   */
  private getErrorType(statusCode: number): ApiErrorType {
    switch (true) {
      case statusCode === ErrorStatusCode.BadRequest:
      case statusCode === ErrorStatusCode.UnprocessableEntity:
        return 'validation_error';
      case statusCode === ErrorStatusCode.Unauthorized:
        return 'authentication_error';
      case statusCode === ErrorStatusCode.Forbidden:
        return 'authorization_error';
      case statusCode === ErrorStatusCode.NotFound:
        return 'not_found_error';
      case statusCode === ErrorStatusCode.Conflict:
        return 'conflict_error';
      case statusCode >= 500:
        return 'server_error';
      default:
        return 'unknown_error';
    }
  }

  /**
   * Extract headers from Response
   */
  private extractHeaders(headers: Headers): Record<string, string> {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  /**
   * Build full URL
   */
  private buildUrl(url: string): string {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return this.baseURL + (url.startsWith('/') ? url : `/${url}`);
  }

  /**
   * Apply request interceptors
   */
  private async applyRequestInterceptors(config: RequestConfig): Promise<RequestConfig> {
    let result = config;
    for (const interceptor of this.requestInterceptors) {
      result = await interceptor(result);
    }
    return result;
  }

  /**
   * Apply response interceptors
   */
  private async applyResponseInterceptors<T>(response: HttpResponse<T>): Promise<HttpResponse<T>> {
    let result = response as HttpResponse<unknown>;
    for (const interceptor of this.responseInterceptors) {
      result = await interceptor(result);
    }
    return result as HttpResponse<T>;
  }

  /**
   * Apply error interceptors
   */
  private async applyErrorInterceptors(error: ApiError): Promise<ApiError> {
    let result = error;
    for (const interceptor of this.errorInterceptors) {
      result = await interceptor(result);
    }
    return result;
  }

  /**
   * Handle error (apply interceptors but don't suppress)
   */
  private async handleError(error: ApiError | unknown): Promise<ApiError> {
    if (!isApiError(error)) {
      return new NetworkError(String(error));
    }
    return error;
  }

  /**
   * Utility to sleep
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Cache management: Invalidate specific cache entry
   */
  invalidateCache(url: string, method: string = 'GET'): boolean {
    return responseCache.invalidate(method, url);
  }

  /**
   * Cache management: Invalidate cache by pattern
   */
  invalidateCachePattern(pattern: string | RegExp): number {
    return responseCache.invalidatePattern(pattern);
  }

  /**
   * Cache management: Clear all cache
   */
  clearCache(): void {
    responseCache.clear();
  }

  /**
   * Cache management: Get cache statistics
   */
  getCacheStats() {
    return responseCache.getStats();
  }
}

/**
 * Type guard
 */
function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
